/**
 * Act VI — The Word Made Flesh (Matthew – John).
 *
 * The promise arrives in person. Four chapters: the birth, the kingdom he
 * announced, the execution, and the morning after the sabbath.
 *
 * Chapter ids and verseIds are stable — progress is persisted against them, so
 * they must not be renamed.
 *
 * Scripture quoted inside scenes is the World English Bible (British Edition),
 * public domain, wrapped in *asterisks* so the renderer can set it in serif.
 */

export const ACT_WORD = {
  id: 'word',
  title: 'The Word Made Flesh',
  summary: 'The promise arrives in person, and is executed for it.',
  span: 'Matthew – John',
  chapters: [
    // ── 1 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-born',
      title: 'Born',
      where: 'Luke 1–2; John 1',
      era: 'c. 5 BC',
      artCaption: 'A guest room that was full, and a hillside of shepherds',
      scenes: [
        "The message goes to a teenage girl in Nazareth. Nazareth is a farming village in the hills of Galilee, a few hundred people at most. The Old Testament never mentions it once. There is a proper Roman town four miles away, and Nazareth lives entirely in its shadow. Not Rome. Not Jerusalem. Not the temple. Before Jesus can walk, the story has already told you whose life it thinks is worth interrupting, and it tells you by the address on the envelope.",

        "Mary is told she will have a son. Her answer is one line. *Let it be done to me according to your word.* The angel's reason is the sentence the chapter turns on: *nothing spoken by God is impossible.* Then she sings, and the song is not about a baby. It is about rulers pulled off their thrones, the hungry fed, the rich sent away with nothing. All of it in the past tense, as though it had already happened. The tune is borrowed from Hannah in 1 Samuel, another woman with an unlikely son.",

        "Then the census, the road south, and the manger. English Bibles say there was no room at the inn. That has put a hard-hearted innkeeper into every nativity play since. Luke's word is *kataluma*, meaning a guest room — the same word he uses for the upper room of the Last Supper. When he means a roadside inn with a keeper, as in the Good Samaritan, he uses a different word, *pandocheion*. So: a crowded family house, not a slammed door. The guest room was full, so the baby went in the feeding trough.",

        "The first people told are shepherds, working nights on the hills outside Bethlehem. Their word counted for little. Some rabbis listed herdsmen with tax collectors as witnesses courts should not trust. The announcement is the surprise. *There is born to you today, in David's city, a Saviour, who is Christ the Lord.* Every important word there was already taken. The Roman emperor was hailed as a saviour on inscriptions, and one from 9 BC dates good news for the world from his birthday. The angel is not inventing the vocabulary. He is taking it back.",

        "John tells the same event from much further back. *In the beginning was the Word, and the Word was with God, and the Word was God.* Those first three words are the opening of Genesis 1:1, and no reader would have missed it. He is starting the Bible again. Then he lands it: *the Word became flesh, and lived among us.* The verb behind lived among us means pitched a tent. God moved in and camped, the way he travelled with Israel in the tent in the desert.",

        "Luke ends the birth story in the temple. A man named Simeon takes the baby, blesses God, then turns to Mary: a sword will pierce your own soul too. Joy and dread are handed over together, and nobody resolves them. The date is untidy too. Herod died in 4 BC, so the birth falls a few years earlier than the calendar that counts from it. A monk did the arithmetic in the sixth century and slipped. Then thirty near-silent years, broken by one story about a boy of twelve who stayed behind.",
      ],
      verseId: 'luk-1-37',
      takeaway: 'The God who spoke creation into being entered it as a baby.',
      questions: [
        {
          id: 'sq-born-1',
          question: 'Who does Luke name as the first people told of the birth?',
          options: [
            'A group of shepherds working outside Bethlehem',
            'The magi who came from the east',
            'Herod and his court in Jerusalem',
            'The priests serving in the temple',
          ],
          answer: 0,
          explanation:
            'Luke 2. The magi appear only in Matthew, and arrive considerably later. Choosing shepherds as first witnesses is a decision about credibility: their word carried little weight, which is exactly what makes the choice deliberate.',
        },
        {
          id: 'sq-born-2',
          question: 'Luke says there was no room for them. What word does he actually use for the place that was full?',
          options: [
            'Pandocheion, the commercial inn with a keeper from the Good Samaritan',
            'Skene, meaning a tent or a temporary booth',
            'Kataluma, a guest room — the same word he later uses for the upper room',
            'Naos, the inner sanctuary of the temple building',
          ],
          answer: 2,
          explanation:
            'Luke knows the word for an inn and uses it elsewhere, which suggests he did not mean one here. The likeliest scene is a packed family home rather than a slammed door — and the innkeeper of the nativity play turns out to have no verse of his own.',
        },
        {
          id: 'sq-born-3',
          question: 'The angel calls the child a Saviour and announces good news. Whose vocabulary was that already?',
          options: [
            'The Jerusalem temple priesthood',
            'The Roman emperor, hailed as saviour on inscriptions that dated good tidings from his birthday',
            'The Greek mystery religions of Asia Minor',
            'The Essene community at Qumran',
          ],
          answer: 1,
          explanation:
            'The Priene inscription of 9 BC uses both terms of Augustus. Applying them to a baby in a feeding trough is not flattery borrowed from Rome — it is a claim staked on Rome’s own ground.',
        },
        {
          id: 'sq-born-4',
          question: 'Which Old Testament verse is John deliberately echoing in "In the beginning was the Word"?',
          options: [
            'Psalm 23:1',
            'Exodus 3:14',
            'Isaiah 53:5',
            'Genesis 1:1',
          ],
          answer: 3,
          explanation:
            'John begins with the opening words of Genesis 1:1 — in the beginning — and then finishes the sentence differently. It is a claim about who Jesus is, made before his narrative has a single event in it.',
        },
      ],
    },

    // ── 2 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-kingdom',
      title: 'The Kingdom Announced',
      where: 'Matthew 5–7; Mark 10',
      era: 'c. AD 27–30',
      artCaption: 'A hillside, a seated teacher, and a city that cannot be hidden',
      scenes: [
        "He begins preaching with one sentence. *Repent! For the Kingdom of Heaven is at hand.* Matthew says kingdom of heaven about thirty times. Mark and Luke tell the same stories and say kingdom of God. They mean the same thing. Matthew writes for Jewish readers who avoided saying God's name out loud, and he puts heaven in its place. The word kingdom is doing hard work too. It does not mean a country with borders. It means a reign — someone actually in charge, actively ruling.",

        "Then he sits down on a hillside and describes it. Almost every line turns the expected order over. Blessed are the poor in spirit. The gentle inherit the earth — and gentle here does not mean weak. It means strength with the brake on. Love your enemies. Do not pile up treasure here. These opening lines are not instructions. They are descriptions: statements about who is already in the better position. That is a much harder thing to argue with than a command.",

        "The crowds are astonished, and Matthew says why. He taught with authority, and not like their scribes. A scribe argued by quoting: Rabbi so-and-so said, on the authority of Rabbi so-and-so. Jesus quotes nobody. He says, you have heard that it was said, but I tell you. He does it six times in one chapter, and each time he makes the law harder rather than easier. Not only murder, but contempt. Not only adultery, but the look. Nobody in that crowd missed what the phrasing implied about the speaker.",

        "Two lines that sound vague are in fact very exact. Turn the other cheek says the right cheek. A right-handed man hitting your right cheek has to use the back of his hand, which was how you insulted an inferior, not how you started a fight. And go the second mile: a Roman soldier could legally force any civilian to carry his kit for one mile, and no further. Offering a second mile is not surrender. It is doing it so freely that the man with the spear stops looking like the one in charge.",

        "He teaches it in stories, and they are small. A mustard seed. A pinch of yeast in dough. Treasure buried in a field that somebody stumbles on and buys. The kingdom in them is never imposed from above. It is hidden, undersized, already working. When his disciples argue about rank, he says whoever wants to be first must be everyone's servant. Then he says why he came, and it is not to teach. *The Son of Man came not to be served, but to serve, and to give his life as a ransom for many.*",

        "What he never does is take power when it is offered. A crowd tries to make him king after a free meal, and he walks off. He says the kingdom has arrived, and also tells people to pray for it to come, and never explains how both are true. A rich, decent young man asks what he must do, is told to sell everything, and leaves sad. The disciples despair. Then who can be saved? The sermon ends without an appeal. It ends with weather: two houses, one storm, and the difference showing only afterwards.",
      ],
      verseId: 'mar-10-45',
      takeaway: 'The kingdom Jesus announced runs upside down — and he came to serve and die.',
      questions: [
        {
          id: 'sq-kingdom-1',
          question: 'What is the one sentence Jesus begins his public preaching with?',
          options: [
            'The temple will be torn down, and not one stone left on another',
            'Follow me, and I will make you fishers of men',
            'Repent, for the kingdom of heaven is at hand',
            'Love your neighbour as yourself',
          ],
          answer: 2,
          explanation:
            'Matthew 4:17. Every other saying in the Gospels sits underneath this one, which is an announcement that something has arrived rather than advice about how to live.',
        },
        {
          id: 'sq-kingdom-2',
          question: 'Matthew 5:41 tells listeners to go a second mile. What was the first mile?',
          options: [
            'The distance a host customarily walked a guest from the door',
            'A Roman soldier’s legal right to make a civilian carry his kit for one mile',
            'The sabbath day’s journey limit set by the rabbis',
            'The walk from Bethany up to Jerusalem',
          ],
          answer: 1,
          explanation:
            'Impressment was a real and resented power, capped at a mile precisely so it could be enforced. Volunteering a second mile turns a humiliation into a choice, which is a different thing from putting up with it.',
        },
        {
          id: 'sq-kingdom-3',
          question: 'Why does Matthew say "kingdom of heaven" where Mark and Luke say "kingdom of God"?',
          options: [
            'He writes for a Jewish readership that avoided speaking the divine name, and puts heaven in its place',
            'He is describing the afterlife rather than the present world',
            'Mark and Luke were translating from Latin sources',
            'The two phrases mark two successive stages of the same kingdom',
          ],
          answer: 0,
          explanation:
            'The phrases are interchangeable — Matthew uses both, and the parallel passages line up word for word. The substitution is a habit of reverence, not a difference of doctrine.',
        },
        {
          id: 'sq-kingdom-4',
          question: 'How does Jesus explain why the Son of Man came?',
          options: [
            'To restore the throne of Israel',
            'To rebuild the temple in three days',
            'To judge the nations',
            'To serve, and to give his life as a ransom for many',
          ],
          answer: 3,
          explanation:
            'Mark 10:45, said to settle an argument among his own disciples about who would rank highest. He answers a question about status with a sentence about his death.',
        },
      ],
    },

    // ── 3 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-cross',
      title: 'The Cross',
      where: 'Isaiah 53; the Passion narratives',
      era: 'c. AD 30',
      artCaption: "Three crosses outside the city, and the sky gone dark at midday",
      scenes: [
        "It happens at Passover, the one week of the year when Jerusalem is unmanageable. The city holds perhaps forty thousand people. At the festival it takes several times that, and Rome moves extra troops in for the occasion. Into that crowd Jesus rides down the Mount of Olives on a borrowed donkey. That is a line from the prophet Zechariah acted out in public — *lowly, and riding on a donkey.* Then he clears the traders out of the temple courts. The authorities have about five days to decide what to do with him.",

        "The turn comes at a meal. Passover follows a script. Every item on the table has a fixed meaning, and the head of the household explains each one in order: this is the bread of affliction our fathers ate in Egypt. Halfway through, Jesus picks up the bread and gives it a new meaning. *Take, eat. This is my body.* Then the cup, which he calls the new covenant in his blood. He is putting himself where Egypt used to be. The meal Christians have eaten ever since starts here, before anything has gone wrong.",

        "Then it moves fast — about eighteen hours start to finish. A garden, where he asks for the cup to be taken away and gets no answer. An arrest by torchlight, a kiss, and every friend he has running into the dark. A religious hearing at night, which broke the rules later written for trials that could end in death. Then a political one at dawn, before a governor who does not want it. The crowd is offered two prisoners and picks Barabbas. His closest friend denies knowing him three times before the cock crows.",

        "Crucifixion deserves plain speaking. Familiarity has worn it smooth. Rome used it on slaves, rebels and conquered peoples, hardly ever on citizens. It was designed to be slow, public, and set up beside a road. The board over his head read King of the Jews in three languages. Rome killed him as a rival king, not as a heretic. Thousands died this way, and almost no bones survive to show it. The exception is a heel bone found in a Jerusalem tomb in 1968, iron nail still through it, bent where it hit a knot.",

        "Every Gospel slows here and reports it flatly, without adjectives. Every one expects you to hear Isaiah underneath it. A servant *pierced for our transgressions, crushed for our iniquities* — and by his wounds we are healed. Written seven hundred years earlier. In John's account, the Passover lambs are being killed that same afternoon. The curtain screening the temple's innermost room tears from the top down. His last words are borrowed from the Psalms. The motive is the Bible's most quoted verse: *God so loved the world, that he gave his one and only Son.*",

        "What the cross actually does has never been settled. The New Testament will not pick one explanation. It reaches for several: a ransom paid, a sacrifice offered, a debt cancelled, a slave bought free. None is ranked above the rest, and Christians have argued the order ever since. What none of them explains away is the moment the Son asks the Father why he has been abandoned. On the Friday evening nothing is resolved. A body goes into a borrowed tomb, lent by a member of the council that condemned him, and the sabbath begins.",
      ],
      verseId: 'isa-53-5',
      takeaway: 'The cross is where the promise, the Passover lamb and the servant all converge.',
      questions: [
        {
          id: 'sq-cross-1',
          question: 'What charge did Rome post above his head?',
          options: [
            'Blasphemer against the temple',
            'King of the Jews',
            'Sorcerer and deceiver of the people',
            'Friend of tax collectors and sinners',
          ],
          answer: 1,
          explanation:
            'John says it was written in three languages so nobody could miss it. Rome did not execute people for theology — the wording is a notice that a rival king has been dealt with.',
        },
        {
          id: 'sq-cross-2',
          question: 'What does Jesus do with the bread and the cup at the Passover meal?',
          options: [
            'He declines them, fasting until the kingdom comes',
            'He follows the prescribed blessing exactly and adds nothing to it',
            'He gives them only to Peter, James and John',
            'He reassigns their meaning, calling the bread his body',
          ],
          answer: 3,
          explanation:
            'Passover was a fixed script in which each element stood for something in the exodus. Changing what the bread means, mid-meal, is a claim about who the rescue is now centred on.',
        },
        {
          id: 'sq-cross-3',
          question: 'What is the best known piece of physical evidence of a crucifixion victim?',
          options: [
            'A heel bone with an iron nail still through it, found in a Jerusalem tomb in 1968',
            'A cross beam sealed inside a tomb near Bethany',
            'A Roman execution register kept at Caesarea',
            'A set of nails held in the temple treasury',
          ],
          answer: 0,
          explanation:
            'Rome crucified thousands, but iron was worth reusing and the executed were rarely given burial, so almost nothing survives — a bare handful of cases has ever been identified. The nail stayed in that man’s heel only because it had bent on a knot and could not be pulled out.',
        },
        {
          id: 'sq-cross-4',
          question: 'Which prophet described a servant "pierced for our transgressions"?',
          options: [
            'Ezekiel',
            'Daniel',
            'Isaiah',
            'Jeremiah',
          ],
          answer: 2,
          explanation:
            'Isaiah 53:5, written centuries before crucifixion existed as a Roman punishment. The Gospel writers quote it without explaining themselves, which suggests they expected their readers to already know it.',
        },
      ],
    },

    // ── 4 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-risen',
      title: 'Risen',
      where: 'Luke 24; John 20–21',
      era: 'c. AD 30',
      artCaption: 'Spices carried to a tomb at first light, and a stone already moved',
      scenes: [
        "The women go to the tomb at dawn on the first day of the week, as soon as the sabbath is over and work is allowed again. They are going to finish the burial rites. Friday had run out before sundown and the job was done in a hurry. They are expecting nothing. They are carrying spices, and the only thing on their minds is practical. Mark records them discussing it on the way: the stone is too heavy, and there is nobody with them strong enough to move it.",

        "It has already been moved, and the body is gone. What they get is not an explanation but a question. Why do you seek the living among the dead? Then six words that Christianity balances on: *He isn't here, but is risen.* And an instruction to remember: he had told them this in Galilee and nobody took it in. An empty tomb proves nothing; a stolen body would look the same. The claim was never that a grave was empty. It is that a particular dead man was later seen, touched, and eaten with.",

        "The disciples do not believe them. Luke says the report struck the men as nonsense, and his word for it is the one doctors used for the babbling of someone out of his mind. These are not people primed for a miracle. They are frightened, grieving men who conclude the women are hysterical. Peter runs to check and comes back baffled rather than convinced. Two of them leave Jerusalem that afternoon, and what they tell a stranger on the road is past tense: *we were hoping that it was he who would redeem Israel.*",

        "Every Gospel names women as the first witnesses. That is remarkable. In that culture a woman's word carried no legal weight in court. It is not a detail anyone would invent. The earliest written summary of the resurrection is a list Paul says he was handed, quoted in 1 Corinthians 15. It leaves the women out and names only men. Historians of every persuasion date that list to within a few years of the crucifixion. The Gospels came later and put the women back in. Somebody kept an awkward memory instead of tidying it away.",

        "The appearances that follow are oddly ordinary. He walks the road to Emmaus explaining, from Moses and the prophets onwards, that the whole Old Testament had been about this. They only recognise him when he breaks the bread. He cooks breakfast on a beach. Thomas has flatly refused to take anyone's word for it, and is told to put a hand into the wound; John never says whether he did. He takes Peter aside and asks him three times whether he loves him. One question, readers have long noticed, for each denial.",

        "The body is not a ghost, and not simply revived. It eats fish and keeps its scars. People who knew him well fail to recognise him. The text does not tidy that up. Nor does Mark. The oldest manuscripts stop at 16:8, with the women running from the tomb terrified, saying nothing to anyone. Later scribes found that so abrupt they added two different endings, and most Bibles print one. The men who ran away were, within weeks, saying it publicly in the city where the grave was. Several died rather than take it back.",
      ],
      verseId: 'luk-24-6',
      takeaway: 'The resurrection is the hinge — everything the church says depends on it being true.',
      questions: [
        {
          id: 'sq-risen-1',
          question: 'Why do the women go to the tomb at dawn?',
          options: [
            'To keep watch in case the body was stolen',
            'To roll the stone away and move the body to a family grave',
            'To finish the burial rites with spices, which the sabbath had cut short',
            'To meet Jesus, who had promised them he would rise',
          ],
          answer: 2,
          explanation:
            'Friday ran out before the work was done and no one may work on the sabbath, so the errand is a chore rather than a vigil. They are worried about the weight of the stone, which is not what you worry about if you expect a resurrection.',
        },
        {
          id: 'sq-risen-2',
          question: 'How do the disciples first receive the women’s report?',
          options: [
            'They dismiss it as nonsense',
            'They believe at once and run to tell the city',
            'They accuse the women of moving the body themselves',
            'They set out immediately for Galilee',
          ],
          answer: 0,
          explanation:
            'Luke’s word for it is the one Greek medical writers used of delirious speech. The first recorded reaction to the resurrection, from the people closest to it, is that somebody is raving.',
        },
        {
          id: 'sq-risen-3',
          question: 'Why do historians treat the women at the tomb as a mark of authenticity?',
          options: [
            'Because only women were permitted to approach a tomb after burial',
            'Because the disciples had already fled to Galilee and nobody else was left',
            'Because Luke wrote his Gospel for a female patron',
            'Because it is the last detail a fabricated account would choose, in a culture where a woman’s word carried no legal weight',
          ],
          answer: 3,
          explanation:
            'This is the criterion of embarrassment: material that works against the teller’s own case is unlikely to have been made up. Paul’s much earlier summary lists only male witnesses, which is what invention normally looks like.',
        },
        {
          id: 'sq-risen-4',
          question: 'Where do the oldest surviving manuscripts of Mark end?',
          options: [
            'With the ascension from the Mount of Olives',
            'At the empty tomb, with the women afraid and saying nothing to anyone',
            'With the breakfast Jesus cooks on the beach',
            'With Thomas putting his hand into the wound',
          ],
          answer: 1,
          explanation:
            'Mark 16:8. Later scribes found the stop so unsatisfying that they wrote two different continuations, and most modern Bibles print the longer one with a note. Whether Mark meant to end there is still argued over.',
        },
      ],
    },
  ],
};
